import { useEffect, useRef } from 'react';
import { useSettings } from '@wu/widget-core';
import type { ThemeSettings } from '../../types';

type ThemeChangeAction = 'open-window' | 'close-window' | 'none';

/**
 * Configuration: what to do when each theme setting changes
 */
const THEME_SETTINGS_BEHAVIOR: Record<keyof ThemeSettings, ThemeChangeAction> =
  {
    launcherButtonColor: 'close-window',
    background: 'open-window',
    headerIconBackgroundColor: 'open-window',
    headerIconColor: 'open-window',
    headerTextColor: 'open-window',
    headerButtonsColor: 'open-window',
    settingsBackgroundColor: 'open-window',
    settingsTextColor: 'open-window',
    settingsAccentColor: 'open-window'
  };

/**
 * Handles window open/close based on theme changes
 */
export function usePreviewThemeChanges({
  onOpenRequest,
  onCloseRequest
}: {
  onOpenRequest: () => void;
  onCloseRequest: () => void;
}) {
  const { editMode, theme } = useSettings();
  const themeRef = useRef(theme);

  useEffect(() => {
    if (!editMode) {
      return;
    }

    const prevType = themeRef.current.type;
    const prevAccentColor = themeRef.current.accentColor;

    // Only handle themeSettings changes when type and accentColor stay the same
    if (theme.type === prevType && theme.accentColor === prevAccentColor) {
      const action = getThemeChangeAction(
        themeRef.current.themeSettings,
        theme.themeSettings
      );

      if (action === 'open-window') {
        onOpenRequest();
      } else if (action === 'close-window') {
        onCloseRequest();
      }
    }

    themeRef.current = theme;
  }, [editMode, theme, onOpenRequest, onCloseRequest]);
}

/**
 * Detects which theme settings changed and returns the action to perform
 */
function getThemeChangeAction(
  prev: ThemeSettings,
  current: ThemeSettings
): ThemeChangeAction {
  let hasOpenTrigger = false;
  let hasCloseTrigger = false;

  for (const key in THEME_SETTINGS_BEHAVIOR) {
    const settingKey = key as keyof ThemeSettings;
    const prevValue = prev[settingKey];
    const currentValue = current[settingKey];

    const hasChanged =
      JSON.stringify(prevValue) !== JSON.stringify(currentValue);

    if (hasChanged) {
      const action = THEME_SETTINGS_BEHAVIOR[settingKey];

      if (action === 'open-window') hasOpenTrigger = true;
      if (action === 'close-window') hasCloseTrigger = true;
    }
  }

  // Priority: open-window > close-window > none
  if (hasOpenTrigger) return 'open-window';
  if (hasCloseTrigger) return 'close-window';

  return 'none';
}
